import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Inbox, ChevronRight, Search } from "lucide-react";
import { StatusBadge, brl, fdate } from "@/pages/portal/Dashboard";

const STATUS_FILTERS = ["", "Rascunho", "Em análise", "Em orçamento", "Orçamento enviado", "Aguardando aprovação",
                        "Aprovado", "Em produção", "Concluído", "Cancelado"];

export default function Solicitacoes() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = async (query = q, st = status) => {
    try {
      const { data } = await api.get("/quote-requests", { params: { q: query, status: st } });
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { load("", ""); }, []);

  const search = (e) => { e.preventDefault(); load(q, status); };
  const changeStatus = (s) => { setStatus(s); load(q, s); };

  return (
    <div data-testid="admin-requests-page">
      <h1 className="display text-3xl font-extrabold uppercase">Orçamentos</h1>
      <p className="mt-1 text-sm text-slate-500">Todas as solicitações recebidas pelo site.</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <form onSubmit={search} className="flex items-center gap-2 flex-1 min-w-[260px]">
          <input
            data-testid="requests-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por protocolo, CPF/CNPJ, nome, e-mail, serviço…"
            className="flex-1 bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600"
          />
          <button data-testid="requests-search-button" className="pill-accent rounded-md px-4 py-2.5">
            <Search className="w-4 h-4" />
          </button>
        </form>
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="requests-status-filters">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s || "all"}
            data-testid={`requests-filter-${s ? s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, "-") : "all"}`}
            onClick={() => changeStatus(s)}
            className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-md border transition-colors ${
              status === s ? "bg-orange-600 border-orange-600 text-white" : "border-slate-700 text-slate-400 hover:border-orange-500"
            }`}
          >
            {s || "Todos"}
          </button>
        ))}
      </div>

      <div className="mt-5 card-tactical rounded-md overflow-hidden">
        {!items ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>
        ) : !items.length ? (
          <div className="py-16 text-center" data-testid="admin-requests-empty">
            <Inbox className="w-10 h-10 text-slate-700 mx-auto" strokeWidth={1.25} />
            <p className="mt-3 text-sm text-slate-500">Nenhuma solicitação encontrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-requests-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="px-5 py-3.5 font-medium">Protocolo</th>
                  <th className="px-5 py-3.5 font-medium">Cliente</th>
                  <th className="px-5 py-3.5 font-medium">Data</th>
                  <th className="px-5 py-3.5 font-medium">Serviço</th>
                  <th className="px-5 py-3.5 font-medium">Peça</th>
                  <th className="px-5 py-3.5 font-medium text-right">Qtd.</th>
                  <th className="px-5 py-3.5 font-medium">Prazo</th>
                  <th className="px-5 py-3.5 font-medium text-right">Valor</th>
                  <th className="px-5 py-3.5 font-medium">Status</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {items.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-900/50 transition-colors" data-testid={`admin-request-row-${q.number}`}>
                    <td className="px-5 py-3.5 mono text-slate-200">{q.number}</td>
                    <td className="px-5 py-3.5">
                      <div className="text-slate-300 max-w-[180px] truncate" title={q.client_name}>{q.client_name}</div>
                      <div className="mono text-[11px] text-slate-600">{q.client_document}</div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-400 whitespace-nowrap">{fdate(q.created_at)}</td>
                    <td className="px-5 py-3.5 text-slate-400 max-w-[200px] truncate" title={(q.services || []).join(" + ")}>
                      {(q.services?.length ? q.services.join(" + ") : q.service_type) || "—"}
                    </td>
                    <td className="px-5 py-3.5 text-slate-400 max-w-[140px] truncate" title={q.part_name}>{q.part_name}</td>
                    <td className="px-5 py-3.5 mono text-right text-slate-300">{q.quantity}</td>
                    <td className="px-5 py-3.5 text-slate-400 whitespace-nowrap">{q.deadline ? fdate(q.deadline) : "—"}</td>
                    <td className="px-5 py-3.5 mono text-right text-slate-200">{q.value != null ? brl(q.value) : "—"}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={q.status} /></td>
                    <td className="px-5 py-3.5 text-right">
                      <Link to={`/solicitacoes/${q.id}`} data-testid={`admin-request-open-${q.number}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-orange-500 hover:text-orange-400">
                        Abrir <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
